import { zodResolver } from "@hookform/resolvers/zod";
import { Control, useForm, UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { useT } from "src/internationalization";
import { operatorCodeMapping } from "src/mappers/operator.mappers";
import { OperatorCode } from "src/types/operator";
import { AppDataType } from "src/types/typedValue";

const propertySchema = z.object({
  code: z.string(),
  type: z.enum(["case", "customer"]),
  dataType: z.custom<AppDataType>(),
  elementCode: z.string().nullable(),
});

export type SimpleRuleDefinitionControl = {
  control: Control<SimleRuleFormData>;
  submit: UseFormHandleSubmit<SimleRuleFormData>;
  hasError: boolean;
  methods: UseFormReturn<SimleRuleFormData>;
  criteria: SimleRuleFormData["criteria"];
};

export type SimleRuleFormData = z.infer<ReturnType<typeof useSchema>>;
const useSchema = () => {
  const t = useT();
  const criterionSchema = z
    .object({
      property: propertySchema.or(z.null()),
      operator: z
        .object({
          code: z.custom<OperatorCode>((operator) =>
            Object.values(operatorCodeMapping).some((o) => o === operator)
          ),
          valueType: z.enum(["argument", "none", "sameAsCurrent", "property"]),
        })
        .or(z.null()),
      value: z.string(),
    })
    .refine((val) => val.property !== null, {
      message: t("page.rules.simple.form.property.required"),
      path: ["property"],
    })
    .refine((val) => val.operator !== null, {
      message: t("page.rules.simple.form.operator.required"),
      path: ["operator"],
    })
    .superRefine((data, ctx) => {
      // Operators 'empty' and 'not_empty' don't require a value
      const operatorNeedsValue =
        data.operator?.code !== "empty" && data.operator?.code !== "nonEmpty";

      if (operatorNeedsValue && data.operator && (!data.value || data.value.trim().length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("page.rules.simple.form.value.required"),
          path: ["value"],
        });
      }
    });
  return z.object({
    criteria: z.array(criterionSchema),
  });
};

export const useSimpleRuleDefinition: () => SimpleRuleDefinitionControl = () => {
  const validationSchema = useSchema();
  const methods = useForm<SimleRuleFormData>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      criteria: [
        {
          property: null,
          operator: null,
          value: "",
        },
      ],
    },
  });

  const criteria = methods.watch("criteria");
  const control = methods.control;

  const hasError = Object.keys(methods.formState.errors).length > 0;

  return {
    methods,
    submit: methods.handleSubmit,
    hasError,
    control,
    criteria,
  };
};
